<template>
  <div class="share-url-component">
    <div :class="['share-url-component__header', headerClass]">
      <div class="share-url-component__button w-100">
        <button :class="buttonClassWithWidth" type="button" :disabled="disabled" @click="openModal">
          <i v-if="iconClass" :class="iconClassWithSpacing"></i>
          {{ label }}
        </button>
      </div>
    </div>

    <teleport to="body">
      <div v-if="modalOpen" class="share-modal" @click.self="closeModal">
        <div class="share-modal__content">
        <div v-if="createdPopupVisible && createdResultCount" class="share-url__created-popup-layer" @click.self="closeCreatedPopup">
          <div class="share-url__created-popup">
            <div class="share-url__created-popup-header">
              <div class="share-url__created-popup-title">
                {{ popupTitleText }}
              </div>
              <button type="button" class="share-url__created-popup-close" :aria-label="translate('share.close', '閉じる')" @click="closeCreatedPopup">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="share-url__created-popup-body">
              <div class="share-url__created-popup-field">
                <div class="share-url__created-popup-label">{{ translate('share.batch_created_count', '発行件数') }}</div>
                <div class="share-url__created-popup-value">{{ createdResultCount }}{{ translate('share.count_unit', '件') }}</div>
              </div>
              <div class="share-url__created-popup-field">
                <label class="form-label mb-2">{{ generatedUrlLabel }}</label>
                <div class="share-url__created-popup-table">
                  <table class="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th scope="col">{{ generatedUrlLabel }}</th>
                        <th scope="col">{{ recipientEmailLabel }}</th>
                        <th scope="col">{{ translate('share.invitation_mail_status_label', 'メール送信') }}</th>
                        <th scope="col"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="result in createdResults" :key="result.share_url || `${result.recipient_email}-${result.id || ''}`">
                        <td class="text-truncate share-url__link-cell">
                          <span>{{ result.share_url || '-' }}</span>
                        </td>
                        <td>{{ result.recipient_email || '-' }}</td>
                        <td>
                          <span
                            v-if="result.invitation_mail_sent !== null"
                            class="badge"
                            :class="result.invitation_mail_sent === false ? 'badge-warning' : 'badge-success'"
                          >
                            {{
                              result.invitation_mail_sent === false
                                ? translate('share.invitation_mail_failed_badge', '失敗')
                                : translate('share.invitation_mail_sent_badge', '送信済み')
                            }}
                          </span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td class="text-end">
                          <button
                            class="btn btn-secondary btn-sm"
                            type="button"
                            :disabled="!result.share_url"
                            @click="copyCreatedResultUrl(result)"
                            :aria-label="translate('share.copy', 'コピー')"
                            :title="translate('share.copy', 'コピー')"
                          >
                            <i class="fas fa-copy"></i>
                            <span class="sr-only">{{ translate('share.copy', 'コピー') }}</span>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-if="createdPasswordVisible" class="share-url__created-popup-field">
                <label class="form-label mb-2">{{ translate('share.password_label', 'パスワード') }}</label>
                <div class="input-group share-url__input-group">
                  <input type="text" class="form-control" v-model="createdPassword" readonly>
                  <button class="btn btn-secondary" type="button" :disabled="!createdPassword" @click="copyCreatedPassword" :aria-label="translate('share.copy', 'コピー')" :title="translate('share.copy', 'コピー')">
                    <i class="fas fa-copy"></i>
                    <span class="sr-only">{{ translate('share.copy', 'コピー') }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!isUploadMode" class="share-modal__header">
          <h5 class="mb-0">{{ modalTitle }}</h5>
        </div>
        <div ref="modalBody" class="share-modal__body">
          <div v-if="flashIsError && flashMessage" class="alert alert-danger py-2 mb-3">
            {{ flashMessage }}
          </div>
          <div v-if="!(flashIsError && flashMessage) && error" class="alert alert-danger py-2">{{ error }}</div>

          <div v-if="!isUploadMode && !isBundleMode" class="share-url__meta mb-3">
            <div class="share-url__meta-item">
              <span class="share-url__meta-label">{{ translate('video.name', 'タイトル') }}</span>
              <span class="share-url__meta-value">{{ displayTitle }}</span>
            </div>
            <div v-if="showFileName" class="share-url__meta-item">
              <span class="share-url__meta-label">{{ translate('video.original_name', 'ファイル名') }}</span>
              <span class="share-url__meta-value">{{ displayFileName }}</span>
            </div>
          </div>

          <div v-if="isBundleFolderMode" class="share-url__meta mb-3">
            <div class="share-url__meta-item">
              <span class="share-url__meta-label">{{ translate('share.bundle_folder_name', 'フォルダ名') }}</span>
              <span class="share-url__meta-value">{{ displayTitle }}</span>
            </div>
          </div>

          <div v-if="isBundleSelectionMode" class="share-url__meta mb-3">
            <div class="share-url__meta-item">
              <span class="share-url__meta-label">{{ translate('share.bundle_item_count', '件数') }}</span>
              <span class="share-url__meta-value">{{ selectedVideoIds.length + selectedFolderIds.length }}{{ translate('share.bundle_count_unit', '件') }}</span>
            </div>
          </div>

          <div v-if="showIncludeSubfoldersOption" class="mb-3">
            <div class="custom-control custom-checkbox">
              <input
                id="includeSubfolders"
                class="custom-control-input"
                type="checkbox"
                v-model="includeSubfolders"
              >
              <label class="custom-control-label" for="includeSubfolders">
                {{ translate('share.bundle_include_subfolders', '子フォルダを含む') }}
              </label>
            </div>
            <div v-if="includeSubfolders" class="alert alert-warning small mt-2 mb-0 py-1 px-2">
              <i class="fas fa-exclamation-triangle mr-1"></i>
              {{ translate('share.bundle_include_subfolders_warning', '選択したフォルダ配下のすべてのサブフォルダ内のファイルが共有対象になります。共有先でダウンロード可能になるため、対象範囲を確認してください。') }}
            </div>
          </div>

          <div v-if="bundleFileListVisible" class="mb-3">
            <button
              type="button"
              class="btn btn-link btn-sm p-0 mb-1"
              @click="bundleFileListExpanded = !bundleFileListExpanded"
            >
              <i :class="bundleFileListExpanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" class="mr-1"></i>
              {{ translate('share.bundle_selected_files_label', '選択した項目一覧') }}（{{ selectedVideoNames.length }}{{ translate('share.bundle_count_unit', '件') }}）
            </button>
            <ul v-if="bundleFileListExpanded" class="list-group list-group-flush small">
              <li v-for="(item, idx) in bundleFileListItems" :key="(item.type || 'video') + '-' + (item.id ?? idx)" class="list-group-item py-1 px-2 d-flex align-items-center justify-content-between">
                <span v-if="item.type === 'folder'">
                  <i class="fas fa-folder mr-1"></i>{{ item.folderName || item.name }}
                </span>
                <span v-else>
                  <span v-if="item.folderName" class="text-muted" style="font-size: 0.75rem;">
                    <i class="fas fa-folder-open mr-1"></i>{{ item.folderName }} /
                  </span>
                  {{ item.name }}
                </span>
                <button type="button" class="btn btn-link btn-sm text-danger p-0 ml-2" @click="removeFromFileList(item)" :title="translate('share.bundle_remove_file', '除外')">
                  <i class="fas fa-times"></i>
                </button>
              </li>
            </ul>
          </div>

          <div v-if="isUploadMode" class="mb-3">
            <label class="form-label">
              {{ translate('share.upload_destination_folder', 'アップロード先フォルダ') }}
              <span class="required-badge">{{ translate('share.required_badge', '必須') }}</span>
            </label>
            <select class="form-control" v-model="selectedFolderId">
              <option :value="null">{{ translate('share.upload_select_folder', 'フォルダを選択してください') }}</option>
              <option v-if="allowRootFolder" value="root">{{ translate('folder.top', 'TOP') }}</option>
              <option v-for="folder in normalizedFolderOptions" :key="folder.id" :value="folder.id">
                {{ folder.label }}
              </option>
            </select>
            <small class="text-muted">{{ translate('share.upload_destination_folder_help', '現在のフォルダが初期選択されています。') }}</small>
          </div>

          <div v-if="isUploadMode && storageOptions.length" class="mb-3">
            <label class="form-label">
              {{ translate('share.storage_type_label', '保存先ストレージ') }}
              <span class="required-badge">{{ translate('share.required_badge', '必須') }}</span>
            </label>
            <select class="form-control" v-model="selectedStorageId">
              <option v-for="opt in storageOptions" :key="opt.id" :value="opt.id">{{ opt.label }}</option>
            </select>
            <div class="alert alert-info small mt-2 mb-0 py-1 px-2">
              <i class="fas fa-info-circle mr-1"></i>
              {{ translate('share.storage_type_note_proxy', '1MB未満のファイルはホット固定です。') }}
            </div>

            <div class="mt-2 p-2 border rounded">
              <div class="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  :id="`uploadSourceDelete-${passwordInputSuffix}`"
                  v-model="sourceDeleteRequested"
                />
                <label class="custom-control-label small" :for="`uploadSourceDelete-${passwordInputSuffix}`">{{ translate('share.source_delete_label', '変換完了後に元ファイルを削除') }}</label>
              </div>
              <small class="form-text text-muted mb-0">{{ translate('share.source_delete_warning', '元ファイルは完全に削除され、復元できません。プレビューは残ります。') }}</small>
            </div>
            <small v-if="sourceDeleteRequested" class="form-text text-muted">{{ translate('share.source_delete_note', 'mxf / mov / mts / mp4 は変換完了後に元ファイルを削除します（保存先の設定は使われません）。プレビューは残ります。') }}</small>
          </div>

          <div v-if="showDownloadTargetSelector" class="mb-3">
            <label class="form-label d-block">{{ translate('share.download_target_label', 'ダウンロード対象') }}</label>
            <div class="custom-control custom-radio mb-2">
              <input
                :id="`downloadTargetSource-${passwordInputSuffix}`"
                class="custom-control-input"
                type="radio"
                :name="`downloadTarget-${passwordInputSuffix}`"
                value="source"
                :disabled="!sourceTargetAvailable"
                v-model="downloadTarget"
              >
              <label
                class="custom-control-label"
                :for="`downloadTargetSource-${passwordInputSuffix}`"
                :class="{ 'text-muted': !sourceTargetAvailable }"
              >
                {{ translate('share.download_target_source', '元ファイル') }}
              </label>
              <small v-if="!sourceTargetAvailable" class="text-muted d-block">{{ sourceUnavailableMessage }}</small>
            </div>
            <div class="custom-control custom-radio">
              <input
                :id="`downloadTargetPreview-${passwordInputSuffix}`"
                class="custom-control-input"
                type="radio"
                :name="`downloadTarget-${passwordInputSuffix}`"
                value="preview"
                :disabled="!previewTargetAvailable"
                v-model="downloadTarget"
              >
              <label
                class="custom-control-label"
                :for="`downloadTargetPreview-${passwordInputSuffix}`"
                :class="{ 'text-muted': !previewTargetAvailable }"
              >
                {{ previewTargetLabel }}
              </label>
              <small v-if="!previewTargetAvailable" class="text-muted d-block">{{ translate('share.share_preview_not_ready', 'プレビューが未生成のため共有できません。') }}</small>
            </div>
          </div>

          <div v-if="isBundleMode" class="alert alert-info small mb-3 py-1 px-2">
            <i class="fas fa-info-circle mr-1"></i>
            {{ bundleExclusionMessage }}
          </div>

          <div class="mb-3">
            <label class="form-label">
              {{ recipientEmailLabel }}
              <span v-if="shareEmailRequired" class="required-badge">{{ translate('share.required_badge', '必須') }}</span>
            </label>
            <textarea
              class="form-control"
              v-model.trim="recipientEmailsInput"
              rows="2"
              required
              :placeholder="translate('share.recipient_email_multiple_placeholder', '例: user1@example.com, user2@example.com')"
            ></textarea>
            <small class="text-muted" v-html="recipientEmailHelp"></small>
            <div class="share-url__csv-upload mt-2">
              <input
                ref="csvFileInput"
                type="file"
                accept=".csv,.txt"
                class="d-none"
                @change="handleCsvFileSelected"
              >
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                :disabled="csvParsing"
                @click="triggerCsvFileInput"
              >
                <i class="fas fa-file-csv mr-1"></i>
                {{ csvParsing
                  ? translate('share.csv_parsing', '読み込み中...')
                  : translate('share.csv_upload_button', 'CSVファイルから読み込み')
                }}
              </button>
              <small class="text-muted d-block mt-1">{{ translate('share.csv_upload_help', 'CSVファイルからメールアドレスを一括読み込みできます') }}</small>
              <div v-if="csvMessage" class="mt-1 small" :class="csvIsError ? 'text-danger' : 'text-success'">
                {{ csvMessage }}
              </div>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label d-block">{{ translate('share.email_gate_mode_label', '認証方法') }}</label>
            <div class="custom-control custom-radio mb-2">
              <input
                :id="`emailGateNone-${passwordInputSuffix}`"
                class="custom-control-input"
                type="radio"
                :name="`emailGateMode-${passwordInputSuffix}`"
                value="none"
                v-model="emailGateMode"
              >
              <label class="custom-control-label" :for="`emailGateNone-${passwordInputSuffix}`">
                {{ translate('share.email_gate_mode_none', '認証なし') }}
              </label>
              <small class="text-muted d-block">{{ translate('share.email_gate_mode_none_help', '同意画面のみで共有内容にアクセスできます。') }}</small>
            </div>
            <div class="custom-control custom-radio mb-2">
              <input
                :id="`emailGateCapture-${passwordInputSuffix}`"
                class="custom-control-input"
                type="radio"
                :name="`emailGateMode-${passwordInputSuffix}`"
                value="capture"
                v-model="emailGateMode"
              >
              <label class="custom-control-label" :for="`emailGateCapture-${passwordInputSuffix}`">
                {{ translate('share.email_gate_mode_capture', 'メール入力のみ（記録）') }}
              </label>
              <small class="text-muted d-block">{{ translate('share.email_gate_mode_capture_help', 'アクセス時にメールアドレスを入力させて記録します。認証は行いません。') }}</small>
            </div>
            <div class="custom-control custom-radio">
              <input
                :id="`emailGateAuth-${passwordInputSuffix}`"
                class="custom-control-input"
                type="radio"
                :name="`emailGateMode-${passwordInputSuffix}`"
                value="auth"
                :disabled="isRecipientEmailsEmpty"
                v-model="emailGateMode"
              >
              <label
                class="custom-control-label"
                :for="`emailGateAuth-${passwordInputSuffix}`"
                :class="{ 'text-muted': isRecipientEmailsEmpty }"
                :style="isRecipientEmailsEmpty ? 'cursor: not-allowed;' : ''"
              >
                {{ translate('share.email_gate_mode_auth', '2段階認証（メールアドレス確認）') }}
              </label>
              <small class="text-muted d-block">{{ translate('share.email_gate_mode_auth_help', '共有先メールアドレスに認証コードを送信して確認します。共有先メールアドレスの入力が必要です。') }}</small>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">
              {{ translate('share.expire_date', '有効期限') }}
              <span v-if="shareExpiryRequired" class="required-badge">{{ translate('share.required_badge', '必須') }}</span>
            </label>
            <DateTimeComponent v-model="expiry" />
          </div>

          <div class="mb-3">
            <label class="form-label">
              {{ accessLimitLabel }}
              <span v-if="shareAccessLimitRequired" class="required-badge">{{ translate('share.required_badge', '必須') }}</span>
            </label>
            <input
              type="number"
              min="1"
              :max="shareAccessLimitRequired ? (accessLimitMax || undefined) : undefined"
              class="form-control"
              v-model.number="accessLimit"
              :required="shareAccessLimitRequired"
              :placeholder="shareAccessLimitRequired ? '' : translate('share.unlimited', '無制限')"
            >
            <small class="text-muted d-block" v-html="shareAccessLimitRequired ? accessLimitHelp : accessLimitOptionalHelp"></small>
            <small v-if="isBundleMode" class="text-muted d-block mt-1" v-html="translate('share.bundle_access_limit_note', '回数制限はファイルごとではなく、ダウンロード回数の合計でカウントされます。')"></small>
            <small v-if="downloadShare" class="text-muted d-block mt-1" v-html="translate('share.attachment_count_in_limit', '添付ファイルのダウンロードも回数制限に含まれます。')"></small>
          </div>

          <div class="mb-3">
            <label class="form-label d-block">{{ translate('share.password_label', 'パスワード') }}</label>
            <div class="share-url__password-block">
              <div class="share-url__password-toggle">
                <div class="custom-control custom-checkbox share-url__password-checkbox">
                  <input
                    :id="passwordOnId"
                    class="custom-control-input"
                    type="checkbox"
                    v-model="passwordRequired"
                    @change="ensurePassword"
                  >
                  <label class="custom-control-label" :for="passwordOnId">
                    {{ translate('share.password_enable_label', 'パスワード保護を有効にする') }}
                  </label>
                </div>
              </div>
              <div v-if="isPasswordEnabled" class="share-url__password-input-group">
                <input type="text" class="form-control" :disabled="!isPasswordEnabled" v-model="password" readonly>
                <button class="btn btn-secondary" type="button" :disabled="!isPasswordEnabled" @click="copyPassword" :aria-label="translate('share.copy', 'コピー')" :title="translate('share.copy', 'コピー')">
                  <i class="fas fa-copy"></i>
                  <span class="sr-only">{{ translate('share.copy', 'コピー') }}</span>
                </button>
                <button class="btn btn-primary" type="button" :disabled="!isPasswordEnabled" @click="generatePassword" :aria-label="translate('button.update', '更新')" :title="translate('button.update', '更新')">
                  <i class="fas fa-sync-alt"></i>
                  <span class="sr-only">{{ translate('button.update', '更新') }}</span>
                </button>
              </div>
              <div class="share-url__include-password-mail mt-2 pl-3">
                <div class="custom-control custom-checkbox">
                  <input
                    :id="'include_password_in_mail_' + passwordInputSuffix"
                    class="custom-control-input"
                    type="checkbox"
                    v-model="includePasswordInMail"
                    :disabled="isIncludePasswordInMailDisabled"
                  >
                  <label class="custom-control-label" :for="'include_password_in_mail_' + passwordInputSuffix">
                    {{ translate('share.include_password_in_mail_label', 'メールにパスワードを記載する') }}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div v-if="isUploadMode" class="mb-3">
            <label class="form-label">{{ translate('share.upload_max_file_size_label', '最大ファイルサイズMB（任意）') }}</label>
            <input
              type="number"
              min="1"
              class="form-control"
              v-model.number="maxFileSizeMb"
            >
            <small class="text-muted">{{ translate('share.upload_max_file_size_help', '未入力時はサイズ上限を設定しません。') }}</small>
          </div>

          <div v-if="isUploadMode" class="mb-3">
            <label class="form-label">{{ translate('share.upload_label_label', 'ラベル（任意）') }}</label>
            <input
              type="text"
              class="form-control"
              v-model.trim="uploadLabel"
              :placeholder="translate('share.upload_label_placeholder', '例: 取材素材アップロード')"
            >
            <small class="text-muted">{{ translate('share.upload_label_help', 'このラベルは、外部受信URLを開いた先ページの見出しに表示されます。') }}</small>
          </div>

          <div class="share-url__result-section">
            <div v-if="entries.length" class="share-url__entries-panel">
              <button
                type="button"
                class="share-url__entries-toggle"
                :aria-expanded="entriesExpanded ? 'true' : 'false'"
                @click="entriesExpanded = !entriesExpanded"
              >
                <span class="share-url__entries-heading">
                  <span class="share-url__entries-title">{{ translate('share.issued_url_list_label', '発行済みURL一覧') }}</span>
                  <span class="share-url__entries-count">（{{ entriesDisplayCount }}{{ translate('share.count_unit', '件') }}）</span>
                </span>
                <span class="share-url__entries-icon" :class="{ 'is-expanded': entriesExpanded }" aria-hidden="true">
                  <i class="fas fa-chevron-down"></i>
                </span>
              </button>

              <div v-if="entriesExpanded" class="share-url__entries-filter">
                <button
                  type="button"
                  class="share-url__entries-filter-toggle"
                  :class="{ 'is-active': showInactiveEntries }"
                  :aria-pressed="showInactiveEntries ? 'true' : 'false'"
                  @click="showInactiveEntries = !showInactiveEntries"
                >
                  <i class="far" :class="showInactiveEntries ? 'fa-check-square' : 'fa-square'" aria-hidden="true"></i>
                  <span>{{ translate('share.show_inactive_urls', '無効化されたURLも表示') }}</span>
                </button>
              </div>

              <div v-if="entriesExpanded && visibleEntries.length" class="table-responsive share-url__entries-table">
                <table class="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th v-if="isBundleSelectionMode" scope="col" class="share-url__nowrap">{{ translate('share.bundle_item_count', '件数') }}</th>
                      <th v-if="showDownloadTargetSelector" scope="col" class="share-url__nowrap">{{ translate('share.download_target_label', 'ダウンロード対象') }}</th>
                      <th scope="col">{{ translate('share.expire_date', '有効期限') }}</th>
                      <th scope="col">{{ recipientEmailLabel }}</th>
                      <th scope="col" class="share-url__nowrap">{{ translate('share.password_label', 'パスワード') }}</th>
                      <th scope="col" class="share-url__nowrap">{{ translate('share.access_limit', '上限') }}</th>
                      <th scope="col" class="share-url__nowrap">{{ remainingCountLabel }}</th>
                      <th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="entry in visibleEntries"
                      :key="entry.id"
                      :class="{'table-warning': isEntryInactive(entry)}"
                    >
                      <td v-if="isBundleSelectionMode" class="share-url__nowrap">{{ entry.item_count != null ? entry.item_count + translate('share.bundle_count_unit', '件') : '-' }}</td>
                      <td v-if="showDownloadTargetSelector" class="share-url__nowrap">
                        <span v-if="entry.download_target === 'preview'" class="badge badge-secondary">{{ translate('share.download_target_preview', 'プレビュー（mp4）') }}</span>
                      </td>
                      <td>{{ entry.expire_date || translate('share.no_expiry', '無期限') }}</td>
                      <td>{{ entry.recipient_email || '' }}</td>
                      <td class="share-url__nowrap">
                        <template v-if="entry.password_plain">
                          <span class="share-url__password-value">
                            <span>{{ revealedPasswords[entry.id] ? entry.password_plain : '●●●●●●●●' }}</span>
                            <button
                              class="btn btn-link btn-sm p-0 ml-1 share-url__password-toggle"
                              type="button"
                              @click="togglePasswordVisibility(entry.id)"
                              :aria-label="revealedPasswords[entry.id] ? translate('share.hide_password', '非表示') : translate('share.show_password', '表示')"
                              :title="revealedPasswords[entry.id] ? translate('share.hide_password', '非表示') : translate('share.show_password', '表示')"
                            >
                              <i class="fas" :class="revealedPasswords[entry.id] ? 'fa-eye-slash' : 'fa-eye'"></i>
                            </button>
                            <button
                              class="btn btn-link btn-sm p-0 ml-1 share-url__password-toggle"
                              type="button"
                              @click="copyToClipboard(entry.password_plain)"
                              :aria-label="translate('share.copy_password', 'コピー')"
                              :title="translate('share.copy_password', 'コピー')"
                            >
                              <i class="fas fa-copy"></i>
                            </button>
                          </span>
                        </template>
                        <template v-else>
                          {{ entry.password_required ? translate('share.password_enabled_badge', 'あり') : translate('share.password_disabled_badge', 'なし') }}
                        </template>
                      </td>
                      <td>{{ entry.access_limit ? `${entry.access_limit}回` : translate('share.unlimited', '無制限') }}</td>
                      <td>{{ entry.access_limit ? `${resolveRemainingCount(entry)}回` : translate('share.unlimited', '無制限') }}</td>
                      <td class="text-end text-nowrap">
                        <div class="share-url__actions">
                          <button v-if="!entry.is_revoked && entry.recipient_email" class="btn btn-outline-primary btn-sm" :disabled="resendingId === entry.id" @click="resendEntry(entry)" :aria-label="translate('share.resend', '再送')">
                            <i class="fas fa-paper-plane"></i>
                            {{ resendingId === entry.id ? translate('share.resending', '送信中...') : translate('share.resend', '再送') }}
                          </button>
                          <button v-if="!entry.is_revoked" class="btn btn-outline-danger btn-sm" @click="revokeEntry(entry)" :aria-label="translate('share.revoke', '無効化')">
                            {{ translate('share.revoke', '無効化') }}
                          </button>
                          <button class="btn btn-secondary btn-sm" @click="copyToClipboard(entry.share_url)" :aria-label="translate('share.copy', 'コピー')">
                            <i class="fas fa-copy"></i>
                            <span class="sr-only">{{ translate('share.copy', 'コピー') }}</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="entriesExpanded && !visibleEntries.length" class="share-url__entries-empty">
                {{ translate('share.active_url_empty', '有効なURLはありません。') }}
              </div>
            </div>
          </div>
        </div>
        <div class="share-modal__footer">
          <button class="btn btn-secondary mr-2" type="button" @click="closeModal">{{ translate('share.close', '閉じる') }}</button>
          <button class="btn btn-primary" type="button" :disabled="saving" @click="submit">
            {{ saving ? translate('share.saving', '保存中...') : translate('share.generate_url', 'URL生成') }}
          </button>
        </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import axios from 'axios';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
import DateTimeComponent from './DateTimeComponent.vue';

export default {
  components: {
    DateTimeComponent,
  },
  props: {
    videoId: {
      type: [Number, String],
      required: true,
    },
    mode: {
      type: String,
      default: 'share',
    },
    title: {
      type: String,
      default: '',
    },
    fileName: {
      type: String,
      default: '',
    },
    showFileName: {
      type: Boolean,
      default: true,
    },
    payloadKey: {
      type: String,
      default: 'videoId',
    },
    addEndpoint: {
      type: String,
      required: true,
    },
    fetchEndpoint: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: '共有URLを発行',
    },
    buttonClass: {
      type: String,
      default: 'btn btn-primary',
    },
    buttonFullWidth: {
      type: Boolean,
      default: true,
    },
    headerClass: {
      type: String,
      default: 'mb-3',
    },
    iconClass: {
      type: String,
      default: '',
    },
    revokeEndpointTemplate: {
      type: String,
      default: '',
    },
    folderOptions: {
      type: Array,
      default: () => [],
    },
    allowRootFolder: {
      type: Boolean,
      default: false,
    },
    initialFolderId: {
      type: [Number, String],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    storageOptions: {
      type: Array,
      default: () => [],
    },
    defaultStorageId: {
      type: [Number, String],
      default: null,
    },
    shareEmailRequired: {
      type: Boolean,
      default: true,
    },
    shareAccessLimitRequired: {
      type: Boolean,
      default: true,
    },
    shareAccessLimitMax: {
      type: Number,
      default: 10,
    },
    shareExpiryRequired: {
      type: Boolean,
      default: true,
    },
    selectedVideoIds: {
      type: Array,
      default: () => [],
    },
    selectedFolderIds: {
      type: Array,
      default: () => [],
    },
    selectedVideoNames: {
      type: Array,
      default: () => [],
    },
    downloadShare: {
      type: Boolean,
      default: false,
    },
    sourceTargetAvailable: {
      type: Boolean,
      default: true,
    },
    previewTargetAvailable: {
      type: Boolean,
      default: false,
    },
    sourceUnavailableReason: {
      type: String,
      default: 'deleted',
    },
  },
  data() {
    return {
      entries: [],
      revealedPasswords: {},
      modalOpen: false,
      expiry: this.shareExpiryRequired ? this.defaultExpiry() : '',
      passwordRequired: false,
      password: '',
      includePasswordInMail: false,
      accessLimit: this.shareAccessLimitRequired ? 1 : '',
      downloadTarget: this.sourceTargetAvailable === false ? 'preview' : 'source',
      recipientEmailsInput: '',
      emailAuthRequired: false,
      emailCaptureRequired: false,
      csvParsing: false,
      csvMessage: '',
      csvIsError: false,
      createdShareUrl: '',
      createdRecipientEmail: '',
      createdResults: [],
      createdPassword: '',
      createdPasswordRequired: false,
      createdPopupVisible: false,
      createdWithEmail: false,
      createdFailedAddresses: [],
      createdMailSentCount: 0,
      createdTotalCount: 0,
      saving: false,
      error: '',
      flashMessage: '',
      flashIsError: false,
      hasLoaded: false,
      entriesExpanded: true,
      resendingId: null,
      entriesTotalCount: 0,
      showInactiveEntries: false,
      passwordInputSuffix: Math.random().toString(36).slice(2, 10),
      selectedFolderId: this.normalizeFolderId(this.initialFolderId),
      maxFileSizeMb: null,
      uploadLabel: '',
      selectedStorageId: this.defaultStorageId,
      sourceDeleteRequested: false,
      includeSubfolders: false,
      bundleFileListExpanded: false,
      bodyScrollState: {
        locked: false,
        scrollY: 0,
        htmlOverflow: '',
        bodyOverflow: '',
        bodyPosition: '',
        bodyTop: '',
        bodyWidth: '',
        backgroundScrollLocks: [],
      },
    };
  },
  computed: {
    isUploadMode() {
      return this.mode === 'upload';
    },
    isBundleMode() {
      return this.mode === 'bundle_folder' || this.mode === 'bundle_selection';
    },
    isBundleFolderMode() {
      return this.mode === 'bundle_folder';
    },
    isBundleSelectionMode() {
      return this.mode === 'bundle_selection';
    },
    showIncludeSubfoldersOption() {
      return this.isBundleFolderMode
        || (this.isBundleSelectionMode && this.selectedFolderIds.length > 0);
    },
    showDownloadTargetSelector() {
      if (this.isBundleMode) {
        return true;
      }
      // クリップ共有(payloadKey='clipId')には元ファイル/プレビュー切替が存在しないため対象外
      return this.downloadShare === true && this.payloadKey === 'videoId';
    },
    sourceUnavailableMessage() {
      if (this.sourceUnavailableReason === 'cold') {
        return this.translate('share.share_source_cold', '元ファイルはコールドストレージまたは移動中のため共有できません。');
      }
      return this.translate('share.share_source_deleted', '元ファイルは削除されているため共有できません。');
    },
    previewTargetLabel() {
      if (this.isBundleMode) {
        return this.translate('share.download_target_preview_mixed', 'プレビュー優先');
      }
      return this.translate('share.download_target_preview', 'プレビュー（mp4）');
    },
    bundleExclusionMessage() {
      if (this.downloadTarget === 'preview') {
        return this.translate('share.bundle_exclusion_note_preview', '動画はプレビュー（mp4）を、プレビューが無いファイルは元ファイルを共有します。どちらも共有できないファイル（コールドストレージ・移動中・元ファイル削除済み）は共有対象外です。');
      }
      return this.translate('share.bundle_exclusion_note', 'コールドストレージ・移動中・元ファイル削除済みのファイルは共有対象外です。');
    },
    bundleFileListVisible() {
      return this.isBundleSelectionMode && this.selectedVideoNames.length > 0;
    },
    bundleFileListItems() {
      return [...this.selectedVideoNames].sort((a, b) => {
        const fa = (a.folderName || '').toLowerCase();
        const fb = (b.folderName || '').toLowerCase();
        if (fa < fb) return -1;
        if (fa > fb) return 1;
        return 0;
      });
    },
    bundleFileListPreview() {
      const max = 5;
      return this.bundleFileListExpanded
        ? this.bundleFileListItems
        : this.bundleFileListItems.slice(0, max);
    },
    bundleFileListHasMore() {
      return this.bundleFileListItems.length > 5;
    },
    isPasswordEnabled() {
      return this.passwordRequired === true;
    },
    isRecipientEmailsEmpty() {
      return (this.recipientEmailsInput || '').trim() === '';
    },
    isIncludePasswordInMailDisabled() {
      return !this.isPasswordEnabled || this.isRecipientEmailsEmpty;
    },
    emailGateMode: {
      get() {
        if (this.emailAuthRequired) return 'auth';
        if (this.emailCaptureRequired) return 'capture';
        return 'none';
      },
      set(mode) {
        this.emailAuthRequired = mode === 'auth';
        this.emailCaptureRequired = mode === 'capture';
      },
    },
    popupTitleKey() {
      if (!this.createdWithEmail) {
        if (this.isUploadMode) return 'share.upload_url_created';
        if (this.isBundleMode) return 'share.bundle_share_url_created';
        return 'share.share_url_created';
      }
      if (this.createdFailedAddresses.length > 0 && this.createdMailSentCount === 0) {
        return 'share.url_all_failed';
      }
      if (this.createdFailedAddresses.length > 0) {
        return 'share.url_partial_sent';
      }
      if (this.isUploadMode) return 'share.upload_url_sent';
      if (this.isBundleMode) return 'share.bundle_url_sent';
      return 'share.share_url_sent';
    },
    popupTitleText() {
      const key = this.popupTitleKey;
      const raw = this.translate(key, '');
      return raw
        .replace(':created', String(this.createdTotalCount))
        .replace(':sent', String(this.createdMailSentCount))
        .replace(':emails', this.createdFailedAddresses.join(', '));
    },
    displayTitle() {
      const value = typeof this.title === 'string' ? this.title.trim() : '';
      return value || '-';
    },
    displayFileName() {
      const value = typeof this.fileName === 'string' ? this.fileName.trim() : '';
      return value || '-';
    },
    modalTitle() {
      if (typeof this.label === 'string') {
        const trimmedLabel = this.label.trim();
        if (trimmedLabel.length > 0) {
          return trimmedLabel;
        }
      }
      if (this.isUploadMode) {
        return this.translate('share.upload_title', 'アップロードURL');
      }
      if (this.isBundleFolderMode) {
        return this.translate('share.bundle_folder_share_modal_title', 'フォルダURL共有');
      }
      if (this.isBundleSelectionMode) {
        return this.translate('share.bundle_selection_share_modal_title', '選択ファイルURL共有');
      }
      return 'URL共有';
    },
    passwordOffId() {
      return `passwordOff-${this.passwordInputSuffix}`;
    },
    passwordOnId() {
      return `passwordOn-${this.passwordInputSuffix}`;
    },
    normalizedFolderOptions() {
      if (!Array.isArray(this.folderOptions)) {
        return [];
      }

      return this.folderOptions
        .map((folder) => {
          const id = Number(folder?.id);
          if (!Number.isFinite(id) || id <= 0) {
            return null;
          }
          const depth = Number(folder?.depth ?? 1);
          const normalizedDepth = Number.isFinite(depth) && depth > 0 ? depth : 1;
          const prefix = '— '.repeat(Math.max(0, normalizedDepth - 1));
          const name = String(folder?.name ?? '').trim();
          return {
            id,
            label: `${prefix}${name || id}`,
          };
        })
        .filter(Boolean);
    },
    generatedUrlLabel() {
      if (this.isUploadMode) {
        return this.translate('share.upload_title', 'アップロードURL');
      }
      return this.translate('share.share_url', '共有URL');
    },
    createdPasswordVisible() {
      return this.createdPasswordRequired && this.createdPassword !== '';
    },
    createdResultCount() {
      return Array.isArray(this.createdResults) ? this.createdResults.length : 0;
    },
    sortedEntries() {
      const entriesWithState = (this.entries || []).map((entry, index) => ({
        entry,
        index,
        inactive: this.isEntryInactive(entry),
      }));

      entriesWithState.sort((a, b) => {
        if (a.inactive === b.inactive) {
          return a.index - b.index;
        }
        return a.inactive ? 1 : -1;
      });

      return entriesWithState.map(({ entry }) => entry);
    },
    visibleEntries() {
      if (this.showInactiveEntries) {
        return this.sortedEntries;
      }
      return this.sortedEntries.filter((entry) => !entry.is_revoked);
    },
    entriesDisplayCount() {
      return this.visibleEntries.length;
    },
    sharedEntriesLabel() {
      if (this.isUploadMode) {
        return this.translate('share.upload_shared_urls', '発行済みアップロードURL');
      }
      return this.translate('share.shared_urls', '共有済みURL');
    },
    remainingCountLabel() {
      if (this.isUploadMode) {
        return this.translate('share.upload_remaining', '残回数');
      }
      return this.translate('share.access_remaining', '残回数');
    },
    accessLimitMax() {
      return this.shareAccessLimitMax || 10;
    },
    accessLimitLabel() {
      return this.translate('share.access_limit_required_label', '回数制限');
    },
    accessLimitHelp() {
      const max = this.accessLimitMax;
      return `1〜${max}の範囲で入力してください。上限は${max}回です。`;
    },
    accessLimitOptionalHelp() {
      return this.translate('share.access_limit_optional_help', '空欄で無制限。入力する場合は1以上を指定してください。');
    },
    recipientEmailLabel() {
      return this.translate('share.recipient_email_label', '共有先メールアドレス');
    },
    recipientEmailHelp() {
      return this.translate('share.recipient_email_help', '改行またはカンマ区切りで複数入力できます。宛先ごとに専用URLを発行し、利用時は同じメールアドレス宛に確認コードを送信します。');
    },
    buttonClassWithWidth() {
      const baseClass = (this.buttonClass || '').trim();
      if (!baseClass) {
        return this.buttonFullWidth ? 'btn btn-primary w-100' : 'btn btn-primary';
      }
      if (!this.buttonFullWidth) {
        return baseClass;
      }
      if (baseClass.includes('w-100') || baseClass.includes('btn-block')) {
        return baseClass;
      }
      return `${baseClass} w-100`;
    },
    iconClassWithSpacing() {
      const trimmed = (this.iconClass || '').trim();
      if (!trimmed) {
        return '';
      }
      return `${trimmed} mr-1`;
    },
  },
  watch: {
    passwordRequired(newVal) {
      if (newVal) {
        this.ensurePassword();
      } else {
        this.password = '';
        this.includePasswordInMail = false;
      }
    },
    recipientEmailsInput(newVal) {
      const isEmptyNow = !newVal || !newVal.trim();
      if (isEmptyNow && this.emailAuthRequired) {
        this.emailAuthRequired = false;
      }
    },
    initialFolderId(newVal) {
      this.selectedFolderId = this.normalizeFolderId(newVal);
    },
    selectedFolderId() {
      if (!this.modalOpen || !this.isUploadMode) {
        return;
      }

      this.fetchEntries();
    },
    modalOpen(newVal) {
      if (newVal) {
        this.lockBodyScroll();
        return;
      }

      this.unlockBodyScroll();
    },
    error(newVal) {
      if (newVal) {
        this.ensureErrorVisible();
      }
    },
    flashMessage(newVal) {
      if (newVal && this.flashIsError) {
        this.ensureErrorVisible();
      }
    },
    flashIsError(newVal) {
      if (newVal && this.flashMessage) {
        this.ensureErrorVisible();
      }
    },
  },
  beforeUnmount() {
    this.unlockBodyScroll();
  },
  emits: ['remove-basket-item', 'share-bundle-created'],
  methods: {
    removeFromFileList(item) {
      this.$emit('remove-basket-item', item);
    },
    lockBodyScroll() {
      if (typeof document === 'undefined' || this.bodyScrollState.locked) {
        return;
      }

      const html = document.documentElement;
      const body = document.body;
      const scrollY = window.scrollY || window.pageYOffset || 0;

      this.bodyScrollState = {
        locked: true,
        scrollY,
        htmlOverflow: html.style.overflow,
        bodyOverflow: body.style.overflow,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyWidth: body.style.width,
        backgroundScrollLocks: this.collectBackgroundScrollLocks(),
      };

      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.width = '100%';
      this.bodyScrollState.backgroundScrollLocks.forEach(({ element }) => {
        element.style.overflow = 'hidden';
        element.style.overscrollBehavior = 'none';
      });
    },
    collectBackgroundScrollLocks() {
      if (typeof document === 'undefined') {
        return [];
      }

      const selectors = ['.content-area', '.file-view', '.details-body'];
      const elements = selectors.flatMap((selector) => Array.from(document.querySelectorAll(selector)));
      const uniqueElements = Array.from(new Set(elements));

      return uniqueElements.map((element) => ({
        element,
        overflow: element.style.overflow,
        overscrollBehavior: element.style.overscrollBehavior,
      }));
    },
    unlockBodyScroll() {
      if (typeof document === 'undefined' || !this.bodyScrollState.locked) {
        return;
      }

      const html = document.documentElement;
      const body = document.body;
      const { scrollY, htmlOverflow, bodyOverflow, bodyPosition, bodyTop, bodyWidth, backgroundScrollLocks } = this.bodyScrollState;

      html.style.overflow = htmlOverflow;
      body.style.overflow = bodyOverflow;
      body.style.position = bodyPosition;
      body.style.top = bodyTop;
      body.style.width = bodyWidth;
      backgroundScrollLocks.forEach(({ element, overflow, overscrollBehavior }) => {
        element.style.overflow = overflow;
        element.style.overscrollBehavior = overscrollBehavior;
      });
      window.scrollTo(0, scrollY);

      this.bodyScrollState = {
        locked: false,
        scrollY: 0,
        htmlOverflow: '',
        bodyOverflow: '',
        bodyPosition: '',
        bodyTop: '',
        bodyWidth: '',
        backgroundScrollLocks: [],
      };
    },
    ensureErrorVisible() {
      if (!this.modalOpen) {
        return;
      }
      this.$nextTick(() => {
        const body = this.$refs.modalBody;
        if (body && typeof body.scrollTo === 'function') {
          body.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    },
    normalizeFolderId(value) {
      if (value === 'root') {
        return (this.isUploadMode && this.allowRootFolder) ? 'root' : null;
      }
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) {
        return null;
      }
      const normalized = Math.trunc(parsed);
      if (normalized <= 0) {
        return null;
      }
      return normalized;
    },
    defaultExpiry() {
      return dayjs().add(24, 'hours').format('YYYY-MM-DD HH:mm');
    },
    isExpired(expireDate) {
      return dayjs(expireDate).isBefore(dayjs());
    },
    isLimitReached(entry) {
      if (!entry || !entry.access_limit) {
        return false;
      }
      const remaining = Number(this.resolveRemainingCount(entry));
      return Number.isFinite(remaining) && remaining <= 0;
    },
    isEntryInactive(entry) {
      if (!entry) {
        return false;
      }
      return entry.is_revoked || this.isExpired(entry.expire_date) || this.isLimitReached(entry);
    },
    isEntryExpiredOrLimitReached(entry) {
      if (!entry) {
        return false;
      }
      return this.isExpired(entry.expire_date) || this.isLimitReached(entry);
    },
    resolveRemainingCount(entry) {
      const accessLimit = Number(entry?.access_limit ?? 0);
      if (!Number.isFinite(accessLimit) || accessLimit <= 0) {
        return null;
      }

      const explicitRemaining = Number(entry?.access_remaining);
      if (Number.isFinite(explicitRemaining)) {
        return Math.max(0, explicitRemaining);
      }

      const uploadCount = Number(entry?.upload_count ?? 0);
      if (!Number.isFinite(uploadCount) || uploadCount < 0) {
        return accessLimit;
      }

      return Math.max(0, accessLimit - uploadCount);
    },
    async fetchEntries() {
      if (this.isUploadMode) {
        const folderId = this.normalizeFolderId(this.selectedFolderId);
        if (folderId === null) {
          this.entries = [];
          this.entriesTotalCount = 0;
          this.hasLoaded = true;
          return;
        }

        try {
          const { data } = await axios.get(this.fetchEndpoint, {
            params: folderId === 'root'
              ? { toRoot: 1 }
              : { folderId },
          });
          this.entries = data.items || [];
          this.entriesTotalCount = Number(data.pagination?.total ?? this.entries.length);
          this.hasLoaded = true;
        } catch (error) {
          console.error('共有一覧の取得に失敗しました', error);
        }
        return;
      }

      if (this.isBundleFolderMode) {
        if (!this.fetchEndpoint || !this.videoId) {
          this.entries = [];
          this.entriesTotalCount = 0;
          this.hasLoaded = true;
          return;
        }

        try {
          const { data } = await axios.get(this.fetchEndpoint + '/' + this.videoId);
          this.entries = data.items || [];
          this.entriesTotalCount = Number(data.pagination?.total ?? this.entries.length);
          this.hasLoaded = true;
        } catch (error) {
          console.error('共有一覧の取得に失敗しました', error);
        }
        return;
      }

      if (!this.fetchEndpoint) {
        this.entries = [];
        this.entriesTotalCount = 0;
        this.hasLoaded = true;
        return;
      }

      try {
        const { data } = await axios.get(this.fetchEndpoint);
        this.entries = data.items || [];
        this.entriesTotalCount = Number(data.pagination?.total ?? this.entries.length);
        this.hasLoaded = true;
      } catch (error) {
        console.error('共有一覧の取得に失敗しました', error);
      }
    },
    openModal() {
      if (this.disabled) {
        return;
      }
      this.resetForm();
      this.modalOpen = true;
      this.fetchEntries();
    },
    closeModal() {
      this.modalOpen = false;
    },
    closeCreatedPopup() {
      this.createdPopupVisible = false;
    },
    resetForm() {
      this.expiry = this.shareExpiryRequired ? this.defaultExpiry() : '';
      this.passwordRequired = false;
      this.password = '';
      this.includePasswordInMail = false;
      this.accessLimit = this.shareAccessLimitRequired ? 1 : '';
      this.downloadTarget = this.sourceTargetAvailable === false ? 'preview' : 'source';
      this.recipientEmailsInput = '';
      this.emailAuthRequired = false;
      this.emailCaptureRequired = false;
      this.csvParsing = false;
      this.csvMessage = '';
      this.csvIsError = false;
      this.createdShareUrl = '';
      this.createdRecipientEmail = '';
      this.createdResults = [];
      this.createdPassword = '';
      this.createdPasswordRequired = false;
      this.createdPopupVisible = false;
      this.createdWithEmail = false;
      this.createdFailedAddresses = [];
      this.createdMailSentCount = 0;
      this.createdTotalCount = 0;
      this.error = '';
      this.saving = false;
      this.entries = [];
      this.entriesExpanded = true;
      this.entriesTotalCount = 0;
      this.showInactiveEntries = false;
      this.hasLoaded = false;
      this.selectedFolderId = this.normalizeFolderId(this.initialFolderId);
      this.maxFileSizeMb = null;
      this.uploadLabel = '';
      this.selectedStorageId = this.defaultStorageId;
      this.sourceDeleteRequested = false;
    },
    ensurePassword() {
      if (this.isPasswordEnabled && !this.password) {
        this.generatePassword();
      }
    },
    generatePassword() {
      const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      let result = '';
      for (let i = 0; i < 8; i += 1) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      this.password = result;
    },
    togglePasswordVisibility(entryId) {
      this.revealedPasswords = {
        ...this.revealedPasswords,
        [entryId]: !this.revealedPasswords[entryId],
      };
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.flash(this.translate('share.clipboard_success', 'コピーしました'));
      } catch (error) {
        this.flash(this.translate('share.clipboard_failed', 'コピーに失敗しました'), true);
      }
    },
    async copyPassword() {
      if (!this.password) return;
      await this.copyToClipboard(this.password);
    },
    async copyCreatedUrl() {
      if (!this.createdShareUrl) return;
      await this.copyToClipboard(this.createdShareUrl);
    },
    async copyCreatedResultUrl(result) {
      if (!result?.share_url) return;
      await this.copyToClipboard(result.share_url);
    },
    async copyCreatedPassword() {
      if (!this.createdPassword) return;
      await this.copyToClipboard(this.createdPassword);
    },
    async resendEntry(entry) {
      const resendEndpoint = entry?.links?.resend;
      if (!resendEndpoint) return;
      if (!confirm(this.translate('share.resend_confirm', '共有URLメールを再送しますか？回数制限がリセットされ、有効期限が現在から7日間に延長されます。'))) return;
      if (entry.include_password_in_mail) {
        if (!confirm(this.translate('share.confirm_include_password_in_mail', 'URLとパスワードを同一メールに記載して送信します。よろしいですか？'))) return;
      }
      this.resendingId = entry.id;
      try {
        await axios.post(resendEndpoint);
        await this.fetchEntries();
        this.flash(this.translate('share.resend_success', '共有URLメールを再送しました。'));
      } catch (error) {
        const msg = error.response?.data?.message;
        this.flash(msg || this.translate('share.resend_failed', '共有URLメールの再送に失敗しました。'), true);
      } finally {
        this.resendingId = null;
      }
    },
    async revokeEntry(entry) {
      const revokeEndpoint = this.resolveRevokeEndpoint(entry);
      if (!revokeEndpoint) return;
      if (!confirm(this.translate('share.revoke_confirm', '本当に無効化しますか？'))) return;
      try {
        await axios.get(revokeEndpoint);
        await this.fetchEntries();
        if (this.isUploadMode) {
          this.flash(this.translate('share.upload_url_revoked', 'アップロードURLを無効化しました'));
        } else {
          this.flash(this.translate('share.revoke_success', '共有URLを無効化しました'));
        }
      } catch (error) {
        if (this.isUploadMode) {
          this.flash(this.translate('share.upload_url_revoke_failed', 'アップロードURLの無効化に失敗しました'), true);
        } else {
          this.flash(this.translate('share.revoke_failed', '共有URLの無効化に失敗しました'), true);
        }
      }
    },
    resolveRevokeEndpoint(entry) {
      if (entry?.links?.revoke) {
        return entry.links.revoke;
      }
      if (!this.revokeEndpointTemplate || !entry?.id) {
        return null;
      }
      return this.revokeEndpointTemplate
        .replace('__UPLOAD_URL_ID__', String(entry.id))
        .replace('__ID__', String(entry.id));
    },
    flash(message, isError = false) {
      this.flashMessage = message;
      this.flashIsError = isError === true;
      setTimeout(() => {
        this.flashMessage = '';
        this.flashIsError = false;
        if (!this.modalOpen) {
          this.error = '';
        }
      }, 3000);
    },
    async submit() {
      this.saving = true;
      this.error = '';
      let expiryMoment = null;
      if (this.shareExpiryRequired || this.expiry) {
        expiryMoment = dayjs(this.expiry, 'YYYY-MM-DD HH:mm', true);
        if (this.shareExpiryRequired && (!this.expiry || !expiryMoment.isValid() || expiryMoment.isBefore(dayjs()))) {
          this.error = this.translate('share.expiry_invalid', '有効期限は現在より後の時刻を指定してください。');
          this.saving = false;
          return;
        }
        if (this.expiry && (!expiryMoment.isValid() || expiryMoment.isBefore(dayjs()))) {
          this.error = this.translate('share.expiry_invalid', '有効期限は現在より後の時刻を指定してください。');
          this.saving = false;
          return;
        }
      }
      if (this.isPasswordEnabled && (!this.password || !/^[A-Za-z0-9]{8}$/.test(this.password))) {
        this.error = this.translate('share.invalid_password', 'パスワードは英数字8桁で設定してください。');
        this.saving = false;
        return;
      }
      if (this.showDownloadTargetSelector) {
        const targetAvailable = this.downloadTarget === 'preview' ? this.previewTargetAvailable : this.sourceTargetAvailable;
        if (!targetAvailable) {
          this.saving = false;
          return;
        }
      }
      if (this.isUploadMode && this.normalizeFolderId(this.selectedFolderId) === null) {
        this.error = this.translate('share.upload_folder_required', 'アップロード先フォルダを選択してください。');
        this.saving = false;
        return;
      }
      if (this.isUploadMode && this.storageOptions.length && (this.selectedStorageId === null || this.selectedStorageId === '')) {
        this.error = this.translate('share.storage_type_required', '保存先ストレージを選択してください。');
        this.saving = false;
        return;
      }
      if (this.shareEmailRequired && !this.recipientEmailsInput) {
        this.error = this.translate('share.recipient_email_required', '共有先メールアドレスを入力してください。');
        this.saving = false;
        return;
      }

      const hasRecipients = !this.isRecipientEmailsEmpty;
      let confirmKey;
      if (this.isUploadMode) {
        confirmKey = hasRecipients ? 'share.confirm_generate_upload_url' : 'share.confirm_create_upload_url';
      } else if (this.isBundleMode) {
        confirmKey = hasRecipients ? 'share.confirm_generate_bundle_url' : 'share.confirm_create_bundle_url';
      } else {
        confirmKey = hasRecipients ? 'share.confirm_generate_download_url' : 'share.confirm_create_download_url';
      }

      if (!window.confirm(this.translate(confirmKey, ''))) {
        this.saving = false;
        return;
      }

      if (this.isPasswordEnabled && this.includePasswordInMail && hasRecipients) {
        if (!window.confirm(this.translate('share.confirm_include_password_in_mail', 'URLとパスワードを同一メールに記載して送信します。よろしいですか？'))) {
          this.saving = false;
          return;
        }
      }

      try {
        let accessLimit = this.accessLimit;
        if (accessLimit === '' || accessLimit === null || Number.isNaN(accessLimit)) {
          accessLimit = null;
        } else {
          accessLimit = Number(accessLimit);
        }
        if (this.shareAccessLimitRequired) {
          if (accessLimit === null || !Number.isInteger(accessLimit) || accessLimit <= 0) {
            this.error = this.translate('share.access_limit_required', `回数制限を1〜${this.accessLimitMax}で入力してください。`);
            this.saving = false;
            return;
          }
        }
        if (accessLimit !== null) {
          if (!Number.isInteger(accessLimit) || accessLimit <= 0) {
            accessLimit = null;
          } else if (this.shareAccessLimitRequired && accessLimit > this.accessLimitMax) {
            this.error = this.translate('share.access_limit_max', `回数制限は${this.accessLimitMax}回以下で設定してください。`);
            this.saving = false;
            return;
          }
        }
        const payload = {
          expiryDate: expiryMoment && expiryMoment.isValid() ? expiryMoment.format('YYYY-MM-DD HH:mm:ss') : null,
          passwordRequired: this.isPasswordEnabled,
          password: this.isPasswordEnabled ? this.password : null,
          includePasswordInMail: this.isPasswordEnabled && this.includePasswordInMail && hasRecipients,
          accessLimit,
          recipientEmails: this.recipientEmailsInput || null,
          emailAuthRequired: this.emailAuthRequired,
          emailCaptureRequired: this.emailCaptureRequired,
        };
        if (this.isUploadMode) {
          const maxFileSizeMb = Number(this.maxFileSizeMb);
          payload.maxFileSize = Number.isFinite(maxFileSizeMb) && maxFileSizeMb > 0
            ? Math.trunc(maxFileSizeMb * 1024 * 1024)
            : null;
          payload.label = this.uploadLabel ? this.uploadLabel.trim() : null;
          const normalizedFolder = this.normalizeFolderId(this.selectedFolderId);
          if (normalizedFolder === 'root') {
            payload.folderId = null;
            payload.toRoot = true;
          } else {
            payload.folderId = normalizedFolder;
          }
          payload.storageId = Number(this.selectedStorageId);
          payload.sourceDeleteRequested = this.sourceDeleteRequested === true;
        }
        if (this.isBundleFolderMode) {
          payload.folderId = this.videoId;
          payload.includeSubfolders = this.includeSubfolders;
        }
        if (this.isBundleSelectionMode) {
          payload.videoIds = this.selectedVideoIds;
          payload.folderIds = this.selectedFolderIds;
          payload.includeSubfolders = this.includeSubfolders;
        }
        if (this.showDownloadTargetSelector) {
          payload.downloadTarget = this.downloadTarget;
        }
        if (!(this.payloadKey in payload) && !this.isBundleMode) {
          payload[this.payloadKey] = this.videoId;
        }

        const { data } = await axios.post(this.addEndpoint, payload);
        const result = data.items || {};
        const created = Array.isArray(result.created) ? result.created : [];
        const failedEntries = Array.isArray(result.failed) ? result.failed : [];
        this.createdResults = created;
        this.createdShareUrl = created.length === 1 ? (created[0].share_url || '') : '';
        this.createdRecipientEmail = created.length === 1 ? (created[0].recipient_email || '') : '';
        this.createdPasswordRequired = created.some((item) => item.password_required === true) || payload.passwordRequired === true;
        this.createdPassword = this.createdPasswordRequired
          ? (payload.password || '')
          : '';

        const mailSentItems = created.filter((item) => item.invitation_mail_sent === true);
        const mailFailedItems = created.filter((item) => item.invitation_mail_sent === false);
        const failedAddresses = [
          ...failedEntries.map((item) => item?.recipient_email).filter(Boolean),
          ...mailFailedItems.map((item) => item?.recipient_email).filter(Boolean),
        ];
        this.createdMailSentCount = mailSentItems.length;
        this.createdFailedAddresses = failedAddresses;
        this.createdTotalCount = created.length + failedEntries.length;
        this.createdWithEmail = mailSentItems.length > 0
          || mailFailedItems.length > 0
          || failedEntries.length > 0;

        this.createdPopupVisible = created.length > 0 || failedEntries.length > 0;
        this.recipientEmailsInput = '';
        if (this.isBundleSelectionMode && created.length > 0) {
          this.$emit('share-bundle-created');
        }
        await this.fetchEntries();
      } catch (error) {
        if (error.response?.data?.message) {
          const errors = Array.isArray(error.response?.data?.errors) ? error.response.data.errors : [];
          if (errors.length > 0) {
            const messages = errors
              .map((item) => {
                const recipientEmail = item?.recipient_email || '-';
                const message = item?.message || '';
                return `${recipientEmail}: ${message}`;
              })
              .filter(Boolean);
            this.error = [error.response.data.message, ...messages].join(' / ');
          } else {
            this.error = error.response.data.message;
          }
        } else {
          if (this.isUploadMode) {
            this.error = this.translate('share.upload_url_create_failed', 'アップロードURLの作成に失敗しました');
          } else {
            this.error = this.translate('share.share_url_create_failed', '共有URLの作成に失敗しました');
          }
        }
      } finally {
        this.saving = false;
      }
    },
    triggerCsvFileInput() {
      if (this.$refs.csvFileInput) {
        this.$refs.csvFileInput.value = '';
        this.$refs.csvFileInput.click();
      }
    },
    async handleCsvFileSelected(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      this.csvParsing = true;
      this.csvMessage = '';
      this.csvIsError = false;

      try {
        const formData = new FormData();
        formData.append('csv_file', file);

        const { data } = await axios.post('/api/share/parse-csv-emails', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const items = data.items || {};
        const emails = items.emails || [];
        const skipped = items.skipped || [];

        if (emails.length === 0) {
          this.csvMessage = this.translate('share.csv_no_emails_found', 'メールアドレスが見つかりませんでした');
          this.csvIsError = true;
          return;
        }

        const newEmails = emails.join('\n');
        if (this.recipientEmailsInput) {
          this.recipientEmailsInput = this.recipientEmailsInput + '\n' + newEmails;
        } else {
          this.recipientEmailsInput = newEmails;
        }

        let message = this.translateWithParams(
          'share.csv_parse_success',
          { count: emails.length },
          `${emails.length}件のメールアドレスを読み込みました`
        );
        if (skipped.length > 0) {
          message += ' / ' + this.translateWithParams(
            'share.csv_parse_skipped',
            { count: skipped.length },
            `${skipped.length}件の行をスキップしました`
          );
        }
        this.csvMessage = message;
        this.csvIsError = false;
      } catch (error) {
        if (error.response?.data?.message) {
          this.csvMessage = error.response.data.message;
        } else {
          this.csvMessage = this.translate('share.csv_parse_failed', 'CSVファイルの読み込みに失敗しました');
        }
        this.csvIsError = true;
      } finally {
        this.csvParsing = false;
      }
    },
    translate(key, fallback) {
      if (typeof this.__ === 'function') {
        const value = this.__(key);
        if (value) {
          return value;
        }
      }
      return fallback;
    },
    translateWithParams(key, params, fallback) {
      let value = this.translate(key, fallback);
      Object.entries(params || {}).forEach(([paramKey, paramValue]) => {
        value = value.replace(new RegExp(`:${paramKey}`, 'g'), String(paramValue));
      });
      return value;
    },
  },
};
</script>

<style scoped>
.share-url__input-group {
  display: flex;
  align-items: center;
}

.share-url__input-group > * + * {
  margin-left: 0.5rem;
}

.share-url__input-group .form-control {
  flex: 1 1 auto;
}

.share-url__created-popup-table {
  overflow-x: auto;
}

.share-url__input-group .btn {
  white-space: nowrap;
}

.share-url__nowrap {
  white-space: nowrap;
}

.share-url__password-value {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.share-url__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.share-url__meta {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
}

.share-url__meta-item {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 0.75rem;
  align-items: start;
}

.share-url__meta-label {
  color: #6c757d;
  font-size: 0.85rem;
  white-space: nowrap;
}

.share-url__meta-value {
  word-break: break-word;
  overflow-wrap: anywhere;
}

.share-url__result-section {
  padding-top: 0;
}

.share-url__created-popup-layer {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(33, 37, 41, 0.28);
}

.share-url__created-popup {
  width: min(100%, 640px);
  border: 1px solid #badbcc;
  border-radius: 0.75rem;
  background: #fff;
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.16);
  overflow: hidden;
}

.share-url__created-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: #f0fff4;
  border-bottom: 1px solid #badbcc;
}

.share-url__created-popup-title {
  color: #0f5132;
  font-weight: 700;
}

.share-url__created-popup-close {
  border: 0;
  background: transparent;
  color: #495057;
}

.share-url__created-popup-body {
  display: grid;
  gap: 1rem;
  padding: 1.25rem;
}

.share-url__created-popup-field {
  display: grid;
  gap: 0.4rem;
}

.share-url__created-popup-label {
  color: #6c757d;
  font-size: 0.85rem;
  font-weight: 600;
}

.share-url__created-popup-value {
  word-break: break-word;
  overflow-wrap: anywhere;
}

.share-modal__content {
  position: relative;
}

.share-url__entries-panel {
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  background: #fff;
  overflow: hidden;
}

.share-url__entries-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.85rem 1rem;
  border: 0;
  background: #f8f9fa;
  color: #212529;
  font-weight: 600;
  text-align: left;
}

.share-url__entries-heading {
  display: inline-flex;
  align-items: baseline;
  gap: 0.2rem;
}

.share-url__entries-title {
  font-size: 0.98rem;
}

.share-url__entries-count {
  color: #6c757d;
  font-size: 0.92rem;
  font-weight: 500;
}

.share-url__entries-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  color: #6c757d;
  transition: transform 0.2s ease;
}

.share-url__entries-icon.is-expanded {
  transform: rotate(180deg);
}

.share-url__entries-table {
  border-top: 1px solid #e9ecef;
}

.share-url__entries-filter {
  padding: 0.5rem 1rem;
  border-top: 1px solid #e9ecef;
  background: #fff;
}

.share-url__entries-filter-toggle {
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: #495057;
  font-size: 0.85rem;
  line-height: 1.4;
}

.share-url__entries-filter-toggle:hover,
.share-url__entries-filter-toggle:focus {
  color: #212529;
  text-decoration: none;
}

.share-url__entries-filter-toggle.is-active {
  color: #0d6efd;
  font-weight: 600;
}

.share-url__entries-empty {
  padding: 0.85rem 1rem;
  border-top: 1px solid #e9ecef;
  color: #6c757d;
  font-size: 0.9rem;
}

.share-url__password-block {
  display: grid;
  gap: 0.75rem;
}

.share-url__password-toggle {
  display: flex;
  align-items: center;
}

.share-url__password-checkbox {
  margin-bottom: 0;
}

.share-url__password-checkbox .custom-control-label {
  cursor: pointer;
  padding-left: 0.25rem;
  font-weight: 500;
}

.share-url__password-input-group {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 0.5rem;
  align-items: center;
}

@media (max-width: 767.98px) {
  .share-url__password-input-group {
    grid-template-columns: minmax(0, 1fr) auto auto;
  }
}
</style>
